import { useEffect, useMemo, useRef, useState, type MouseEvent, type MutableRefObject } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import type { QuestionResponse, SkipAnswer } from '@assessments/shared'
import { evaluateVisibility, SectionCompletionState } from '@assessments/shared'
import { cn } from '@/lib/utils'
import { RichText } from '@/components/ui/RichText'
import { ProgressBar } from '@/components/ui/progress-bar'
import { isSessionExpired } from '../../services/apiError.js'
import { useSession } from '../auth/SessionContext.js'
import { canEditAssessment } from '../auth/permissions.js'
import { recordResponse, useResponsesAutosave, type SaveState } from '../response-autosave/index.js'
import { useSectionView } from './useSectionView.js'
import { useSectionCompletion } from './useSectionCompletion.js'
import { useSectionLock } from './useSectionLock.js'
import { QuestionList } from './QuestionList.js'
import { DoneEditing } from './DoneEditing.js'
import { deriveSectionProgress } from './sectionProgress.js'
import { pendingAnswerWrites, toInitialResponse, toRecordRequest } from './questionMapping.js'
import {
  CLEARED_REQUEST,
  deriveHiddenQuestionIds,
  emptyResponse,
  hasAnswer,
  liveAnswers,
  withoutHidden,
} from './skipVisibility.js'
import { deriveVisibleSupportingTexts, EMPTY_SUPPORTING_TEXTS } from './supportingTextVisibility.js'

/** Matches two runs of the `highlight-flash` keyframes (tailwind.config.cjs). */
const HIGHLIGHT_MS = 2000

/** Leaves the section for `path` without stranding an in-flight autosave. */
export type SectionExit = (path: string) => Promise<void>

export interface SectionViewProps {
  organizationSurveyId: string
  sectionId: string
  /** Filled with this view's exit so navigation rendered outside it (the assessment
   * sidebar) leaves the section the same way its own exits do. */
  exitRef?: MutableRefObject<SectionExit | null>
}

/**
 * The first unanswered required question in section display order (subSections[]
 * order, each sub-section's questions[] order), and the sub-section containing it.
 * Null when nothing is unanswered.
 */
function firstUnansweredTarget(
  subSections: Array<{ id: string; questions: Array<{ id: string }> }>,
  unansweredRequired: string[],
): { subSectionId: string; questionId: string } | null {
  const unanswered = new Set(unansweredRequired)
  for (const sub of subSections) {
    for (const question of sub.questions) {
      if (unanswered.has(question.id)) return { subSectionId: sub.id, questionId: question.id }
    }
  }
  return null
}

/**
 * The editable section view: the active sub-section's questions with their saved
 * answers and role-gated inputs, wrapped in a top action bar, a prefill review banner
 * and a sub-section sidebar the respondent can move through freely.
 *
 * Answers autosave — text on blur, a selection on change, never per keystroke — and
 * progress counts the loaded answers plus every save confirmed since, so a bar moves
 * only once an answer is actually stored. "Save Progress" persists what autosave
 * hasn't; Mark complete runs that same persist step first, then the completion gate.
 */
export function SectionView({ organizationSurveyId, sectionId, exitRef }: SectionViewProps) {
  const { data, loading, error, reload } = useSectionView(organizationSurveyId, sectionId)
  const { session, onSessionExpired } = useSession()
  const completion = useSectionCompletion(organizationSurveyId, sectionId, onSessionExpired)
  // A retired industry refuses the save; reloading brings the current choices for that
  // question, while every other edited answer keeps its local value (see the [data] effect).
  const refreshedQuestionId = useRef<string | null>(null)
  const [revisions, setRevisions] = useState<Record<string, number>>({})
  // The server clears answers a save hides (in any section); applied once defined below.
  // An API from before skip logic returns no clearedQuestionIds.
  const applyClearedRef = useRef<(questionIds: string[]) => void>(() => {})
  const autosave = useResponsesAutosave(organizationSurveyId, onSessionExpired, {
    onSaved: (result) => applyClearedRef.current(result?.clearedQuestionIds ?? []),
    onQuestionHidden: reload,
    onIndustryUnavailable: (questionId) => {
      refreshedQuestionId.current = questionId
      reload()
    },
  })
  const navigate = useNavigate()

  // Company Administrator (org admin) and Administrator (Ethisphere admin) get the
  // completion actions; Org View Only (`User`) is read-only and never engages the lock.
  const canEdit = canEditAssessment(session?.user.roles)
  const sectionLock = useSectionLock(organizationSurveyId, sectionId, canEdit, onSessionExpired)
  const isComplete = completion.state === SectionCompletionState.Complete
  // While the acquire is still in flight, the read's isEditableByMe seeds the banner
  // so a section held by another admin never flashes as plainly disabled first.
  const blockedByLock =
    canEdit &&
    (sectionLock.status === 'blocked' ||
      sectionLock.status === 'lost' ||
      sectionLock.status === 'stale' ||
      (sectionLock.status === 'acquiring' && data?.isEditableByMe === false))
  // An editor without the lock still needs a way out: the completion footer below is
  // gated on holding it, so on loss it disappears along with Done editing.
  const strandedWithoutLock = canEdit && !sectionLock.isHeld && sectionLock.status !== 'acquiring'
  const [submitting, setSubmitting] = useState(false)
  // True while "Done editing" runs its own persist/release flow.
  const [exiting, setExiting] = useState(false)
  // A direct write reads the answers back only after autosave's flush resolves, so an
  // edit landing mid-flow would be overwritten by the value the flow already had.
  const persisting = submitting || exiting
  // Complete sections are read-only until re-opened; editing also requires the lock, and
  // pauses while a direct write is in flight.
  const editable = canEdit && !isComplete && sectionLock.isHeld && !persisting

  const [activeSubSectionId, setActiveSubSectionId] = useState<string | null>(null)
  const [responses, setResponses] = useState<Record<string, QuestionResponse>>({})
  // Read by the direct-write paths after they await the flush, where the render-time
  // `responses` binding would be a stale snapshot.
  const responsesRef = useRef(responses)
  responsesRef.current = responses
  // Question ids edited since the last load/persist — used by Mark complete to also
  // send a cleared (now-empty) answer that autosave wouldn't count as answered.
  const dirty = useRef<Set<string>>(new Set())
  // One answers map serves skip logic and supporting texts: saved answers from other
  // sections (both features' trigger pools) under this section's live answers. Either
  // feature's payload is absent from an API that predates it.
  const answers = useMemo(
    () =>
      data
        ? liveAnswers(
            [
              ...(data.skipLogic?.triggerAnswers ?? []),
              ...(data.supportingTexts?.triggerAnswers ?? []),
            ],
            data.subSections,
            responses,
          )
        : new Map<string, SkipAnswer>(),
    [data, responses],
  )
  const hidden = useMemo(
    () =>
      data?.skipLogic?.rules.length
        ? evaluateVisibility(data.skipLogic.rules, answers)
        : new Set<string>(),
    [data, answers],
  )
  const hiddenRef = useRef(hidden)
  hiddenRef.current = hidden
  const supportingTexts = useMemo(
    () =>
      data
        ? deriveVisibleSupportingTexts(data.supportingTexts?.blocks ?? [], answers, hidden)
        : EMPTY_SUPPORTING_TEXTS,
    [data, answers, hidden],
  )
  // Persistence failure from the submit's save step — distinct from the completion gate.
  const [saveError, setSaveError] = useState(false)
  // Focused once its sub-section is active and rendered, not before.
  const [pendingFocusQuestionId, setPendingFocusQuestionId] = useState<string | null>(null)
  const [highlightedQuestionId, setHighlightedQuestionId] = useState<string | null>(null)
  const highlightFrame = useRef<number | null>(null)
  const alertsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!data) return
    setActiveSubSectionId((current) => current ?? data.subSections[0]?.id ?? null)
    const refreshed = refreshedQuestionId.current
    refreshedQuestionId.current = null
    // The question's inputs hold their own picks, so they rebuild from the reloaded answer.
    if (refreshed) setRevisions((prev) => ({ ...prev, [refreshed]: (prev[refreshed] ?? 0) + 1 }))
    // A reload must not overwrite answers edited since load (unsaved, failed or in flight).
    const local = responsesRef.current
    const kept = new Set<string>()
    const seed: Record<string, QuestionResponse> = {}
    for (const sub of data.subSections) {
      for (const question of sub.questions) {
        const edited = local[question.id]
        if (edited && question.id !== refreshed && dirty.current.has(question.id)) {
          seed[question.id] = edited
          kept.add(question.id)
        } else {
          seed[question.id] = toInitialResponse(question)
        }
      }
    }
    dirty.current = kept
    setResponses(seed)
  }, [data])

  applyClearedRef.current = (questionIds) => {
    if (questionIds.length === 0) return
    setResponses((prev) => {
      const next = { ...prev }
      for (const id of questionIds) {
        const current = next[id]
        if (current) next[id] = emptyResponse(current)
      }
      return next
    })
    for (const id of questionIds) {
      dirty.current.delete(id)
      autosave.markSaved(id, CLEARED_REQUEST)
    }
  }

  function handleChange(questionId: string, response: QuestionResponse) {
    const next = { ...responsesRef.current, [questionId]: response }
    // A question this change hides loses its answer straight away; the save below
    // clears it on the server too. Answers only render once the section has loaded.
    const nowHidden = deriveHiddenQuestionIds(data!.skipLogic, data!.subSections, next)
    for (const id of nowHidden) {
      const current = next[id]
      if (!hiddenRef.current.has(id) && current && hasAnswer(current)) {
        next[id] = emptyResponse(current)
        dirty.current.delete(id)
      }
    }
    responsesRef.current = next
    setResponses(next)
    dirty.current.add(questionId)
    if (saveError) setSaveError(false)
    if (response.kind === 'text') {
      // Text is held in the browser as the respondent types; it autosaves on blur.
      autosave.invalidate(questionId)
    } else if (editable) {
      autosave.save(questionId, toRecordRequest(response))
    }
  }

  // Selections already saved on change, and an untouched field must not create an
  // empty answer.
  function handleBlur(questionId: string) {
    if (!editable) return
    const response = responses[questionId]
    if (!response || response.kind !== 'text') return
    if (!dirty.current.has(questionId) || autosave.isClean(questionId)) return
    autosave.save(questionId, toRecordRequest(response))
  }

  // Persists every answered question the section holds but hasn't yet stored — not just
  // this session's edits — so prefilled/existing answers are written as real respondent
  // answers too (see pendingAnswerWrites). Waits out any autosave first: these writes
  // bypass autosave's per-question coalescing, so an earlier one still in flight could
  // otherwise land last and restore the value it was carrying.
  async function persistPendingAnswers(): Promise<boolean> {
    try {
      await autosave.flush()
      const toPersist = pendingAnswerWrites(
        withoutHidden(responsesRef.current, hiddenRef.current),
        dirty.current,
        autosave.isClean,
      )
      const results = await Promise.all(
        toPersist.map(([id, request]) => recordResponse(organizationSurveyId, id, request)),
      )
      for (const [id, request] of toPersist) autosave.markSaved(id, request)
      applyClearedRef.current(results.flatMap((r) => r?.clearedQuestionIds ?? []))
      dirty.current = new Set()
      return true
    } catch (err) {
      // A dead session is not a persistence failure — reporting it as one would invite
      // the respondent to retry a save that can never succeed.
      if (isSessionExpired(err)) onSessionExpired()
      else setSaveError(true)
      return false
    }
  }

  async function handleSaveProgress() {
    setSaveError(false)
    setSubmitting(true)
    await persistPendingAnswers()
    setSubmitting(false)
  }

  async function handleMarkComplete() {
    setSaveError(false)
    setSubmitting(true)
    const persisted = await persistPendingAnswers()
    setSubmitting(false)
    if (!persisted) return
    if (await completion.markComplete()) {
      // Completion is a terminal exit — release the lock before leaving. On failure the
      // lock stays 'held' (matching the still-live server lock), so stay put rather than
      // navigate away from a section this tab still appears to hold; the unmount/pagehide
      // cleanup gets another chance to release it once the user does leave.
      try {
        await sectionLock.release()
      } catch {
        return
      }
      navigate(data!.overviewPath)
    }
  }

  // Leaving unmounts the section and its cleanup releases the lock, so an autosave still
  // in flight would reach the lock-guarded write path after the release and be rejected.
  async function exitSection(path: string) {
    if (sectionLock.isHeld) {
      await autosave.flush()
      try {
        await sectionLock.release()
      } catch {
        return
      }
    }
    navigate(path)
  }

  function exitSectionOnClick(path: string) {
    return (event: MouseEvent<HTMLAnchorElement>) => {
      // Leave modified clicks (open in new tab/window) to the browser.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return
      event.preventDefault()
      void exitSection(path)
    }
  }

  useEffect(() => {
    if (!exitRef) return
    exitRef.current = exitSection
    return () => {
      exitRef.current = null
    }
  })

  const progress = useMemo(
    () => deriveSectionProgress(data?.subSections ?? [], autosave.confirmed, hidden),
    [data, autosave.confirmed, hidden],
  )

  const activeSubSection = useMemo(() => {
    if (!data) return null
    return data.subSections.find((s) => s.id === activeSubSectionId) ?? data.subSections[0] ?? null
  }, [data, activeSubSectionId])

  const activeSubSectionPosition = useMemo(() => {
    if (!data || !activeSubSection) return null
    const index = data.subSections.findIndex((s) => s.id === activeSubSection.id)
    return index === -1 ? null : index + 1
  }, [data, activeSubSection])

  // Rolled up across every question: any error wins (something needs a retry), then
  // any in-flight save, then "saved".
  const globalSaveState: SaveState = submitting
    ? 'saving'
    : Object.values(autosave.status).includes('error')
      ? 'error'
      : Object.values(autosave.status).includes('saving')
        ? 'saving'
        : Object.values(autosave.status).includes('saved')
          ? 'saved'
          : 'idle'

  // A required question the server flagged may have been hidden since by a later
  // answer, so every blocked-completion surface reads from this one filtered list.
  const visibleUnansweredRequired = useMemo(
    () => completion.unansweredRequired.filter((id) => !hidden.has(id)),
    [completion.unansweredRequired, hidden],
  )
  // The blocked-completion count is section-wide but the per-question flags only render
  // inside the active sub-section, so without a per-sub-section breakdown the respondent
  // cannot reconcile the number with what is on screen.
  const unansweredBySubSection = useMemo(() => {
    const unanswered = new Set(visibleUnansweredRequired)
    const counts = new Map<string, number>()
    for (const sub of data?.subSections ?? []) {
      const count = sub.questions.filter((q) => unanswered.has(q.id)).length
      if (count > 0) counts.set(sub.id, count)
    }
    return counts
  }, [data, visibleUnansweredRequired])

  // Focuses a pending "jump to first" target once its sub-section has rendered —
  // needed because switching activeSubSectionId only takes effect on the next render.
  useEffect(() => {
    if (!pendingFocusQuestionId) return
    const questionId = pendingFocusQuestionId
    const el = document.getElementById(`question-${questionId}`)
    if (!el) return
    // jsdom implements neither method's scrolling, hence the optional call.
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
    // Focusing scrolls by default, which would jump past the smooth scroll above.
    el.focus({ preventScroll: true })
    setPendingFocusQuestionId(null)
    // Clear first, then re-apply a frame later, so jumping to the same question twice
    // replays the flash rather than leaving a finished animation's class in place.
    // The frame is cancelled only on unmount — clearing pendingFocusQuestionId above
    // re-runs this effect, and cancelling there would kill the frame before it fires.
    setHighlightedQuestionId(null)
    highlightFrame.current = requestAnimationFrame(() => setHighlightedQuestionId(questionId))
  }, [pendingFocusQuestionId, activeSubSection])

  useEffect(
    () => () => {
      if (highlightFrame.current !== null) cancelAnimationFrame(highlightFrame.current)
    },
    [],
  )

  useEffect(() => {
    if (!highlightedQuestionId) return
    const timer = window.setTimeout(() => setHighlightedQuestionId(null), HIGHLIGHT_MS)
    return () => window.clearTimeout(timer)
  }, [highlightedQuestionId])

  // Every Mark complete outcome the respondent must read renders below the action, so
  // bring it into view rather than leaving them to find it. Each attempt clears the
  // previous outcome first, so a repeat failure re-runs this.
  const markCompleteFailed =
    saveError || visibleUnansweredRequired.length > 0 || Boolean(completion.error)
  useEffect(() => {
    if (!markCompleteFailed) return
    alertsRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
  }, [markCompleteFailed])

  function handleJumpToFirst() {
    if (!data) return
    const target = firstUnansweredTarget(data.subSections, visibleUnansweredRequired)
    if (!target) return
    if (target.subSectionId !== activeSubSectionId) setActiveSubSectionId(target.subSectionId)
    setPendingFocusQuestionId(target.questionId)
  }

  // A failed or in-flight reload keeps the loaded section on screen, so the respondent's
  // unsaved answers stay where they can still be saved.
  if (!data && loading) {
    return (
      <main className="min-w-0 flex-1 px-6 py-10 text-sm text-subtle lg:px-10">
        Loading section…
      </main>
    )
  }
  if (!data) {
    return (
      <main className="min-w-0 flex-1 px-6 py-10 lg:px-10">
        <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-subtle">
          Couldn’t load this section. Please try again.
        </div>
      </main>
    )
  }

  const { reviewBanner } = data

  const showCompletionActions = canEdit && !completion.loading && sectionLock.isHeld

  return (
    // Gutter away from the assessment sidebar (px) matches the overview page's main
    // content; without it, content sits flush against the sidebar's edge.
    <main className="min-w-0 flex-1 px-6 py-10 lg:px-10">
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-border pb-4">
          <nav aria-label="Breadcrumb" className="flex items-center gap-4 text-sm">
            {/* Below lg the assessment sidebar, which carries "All Assessments", is hidden. */}
            <Link
              to="/"
              onClick={exitSectionOnClick('/')}
              className="text-subtle underline-offset-2 hover:text-body hover:underline lg:hidden"
            >
              ← All Assessments
            </Link>
            <Link
              to={data.overviewPath}
              onClick={exitSectionOnClick(data.overviewPath)}
              className="text-subtle underline-offset-2 hover:text-body hover:underline"
            >
              ← Back to overview
            </Link>
          </nav>

          {/* Save status and section actions stay together at the right edge, even when
            the bar wraps or only some of them are shown. */}
          <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
            {/* Ambient "is it saved" status only — an error already gets its own, more
              specific banner (per-question retry, or the section-wide alert below), so
              this never duplicates that message. */}
            {(globalSaveState === 'saving' || globalSaveState === 'saved') && (
              <span className="flex items-center gap-2 text-sm text-body">
                <span
                  className={cn(
                    'h-2 w-2 rounded-full',
                    globalSaveState === 'saving' ? 'bg-info-surface' : 'bg-success',
                  )}
                  aria-hidden="true"
                />
                {globalSaveState === 'saving' ? 'Saving…' : 'Saved just now'}
              </span>
            )}

            {showCompletionActions && !isComplete && (
              <button
                type="button"
                onClick={handleSaveProgress}
                disabled={completion.pending || submitting}
                className="rounded-button border border-border bg-surface-page px-4 py-2 text-sm font-medium text-body hover:bg-surface-hover disabled:opacity-60"
              >
                Save Progress
              </button>
            )}

            {showCompletionActions &&
              (isComplete ? (
                <span className="rounded-button bg-success/10 px-4 py-2 text-sm font-semibold text-success">
                  ✓ Complete
                </span>
              ) : (
                <button
                  type="button"
                  onClick={handleMarkComplete}
                  disabled={completion.pending || submitting}
                  className="rounded-button bg-primary px-4 py-2 text-sm font-semibold text-primary-on hover:opacity-90 disabled:opacity-60"
                >
                  {submitting ? 'Saving…' : 'Mark complete'}
                </button>
              ))}
          </div>
        </div>

        <h1 className="mb-3 text-[24px] font-semibold tracking-tight text-body">
          {data.sectionTitle}
        </h1>

        <RichText html={data.sectionInstruction} className="mb-4" />

        {blockedByLock && (
          <div
            role="status"
            className="mb-4 rounded-lg border border-border bg-surface-hover p-3 text-sm text-body"
          >
            {sectionLock.status === 'lost' || sectionLock.status === 'stale' ? (
              <p>
                🔒 Your editing session ended and this section is read-only again. Anything you
                changed since your last save wasn’t kept.
              </p>
            ) : (
              <p>
                🔒 This section is being edited by another admin. You can read it, but editing is
                unavailable until they finish.
              </p>
            )}
            <p className="mt-1 text-subtle">
              Editing frees up after a few minutes of inactivity.{' '}
              <button
                type="button"
                onClick={() => sectionLock.retry()}
                className="font-medium text-primary underline underline-offset-2"
              >
                Check again
              </button>
            </p>
          </div>
        )}

        {canEdit && sectionLock.status === 'submitted' && (
          <div
            role="status"
            className="mb-4 rounded-lg border border-border bg-surface-hover p-3 text-sm text-body"
          >
            This assessment has been submitted and can no longer be edited.
          </div>
        )}

        {canEdit && sectionLock.status === 'unauthenticated' && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-danger bg-danger/10 p-3 text-sm text-body"
          >
            Your session expired, so this section is read-only and your editing session was
            released. Sign in again to keep editing.
          </div>
        )}

        {canEdit && sectionLock.status === 'forbidden' && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-danger bg-danger/10 p-3 text-sm text-body"
          >
            You no longer have permission to edit this section, so it is now read-only. Contact your
            administrator if you think this is wrong.
          </div>
        )}

        {canEdit && sectionLock.status === 'error' && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-danger bg-danger/10 p-3 text-sm text-body"
          >
            Couldn’t start your editing session — the section is read-only for now.{' '}
            <button
              type="button"
              onClick={() => sectionLock.retry()}
              className="font-medium text-primary underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        )}

        {error && !loading && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-danger bg-danger/10 p-3 text-sm text-body"
          >
            Couldn’t refresh this section.{' '}
            <button
              type="button"
              onClick={() => reload()}
              className="font-medium text-primary underline underline-offset-2"
            >
              Try again
            </button>
          </div>
        )}

        {reviewBanner.hasPrefillForReview && (
          <div className="mb-4 rounded-lg border border-info bg-info-surface/10 p-3 text-sm text-body">
            {reviewBanner.count} prefilled {reviewBanner.count === 1 ? 'answer' : 'answers'} ready
            for your review.
          </div>
        )}

        <div className="flex flex-col gap-6 md:flex-row">
          {/* Left column: sub-section navigation, plus the section-level progress bar
            pinned beneath it (not part of the "Sub-sections" nav landmark itself). */}
          <div className="shrink-0 md:w-56">
            <nav aria-label="Sub-sections">
              <ul className="flex flex-col gap-1">
                {data.subSections.map((sub) => {
                  const isActive = sub.id === activeSubSection?.id
                  const unansweredHere = unansweredBySubSection.get(sub.id) ?? 0
                  return (
                    <li key={sub.id}>
                      <button
                        type="button"
                        aria-current={isActive ? 'true' : undefined}
                        onClick={() => setActiveSubSectionId(sub.id)}
                        className={cn(
                          'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors',
                          isActive
                            ? 'bg-surface-hover font-semibold text-body'
                            : 'text-subtle hover:bg-surface-hover/50',
                        )}
                      >
                        <span className="block truncate">{sub.title}</span>
                        <ProgressBar
                          answered={progress.bySubSection.get(sub.id)?.answered ?? 0}
                          total={progress.bySubSection.get(sub.id)?.total ?? 0}
                          label={`${sub.title} progress`}
                          size="sm"
                          className="mt-1"
                        />
                        {unansweredHere > 0 && (
                          <span className="mt-0.5 block text-xs font-medium text-destructive">
                            {unansweredHere} {unansweredHere === 1 ? 'needs' : 'need'} an answer
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </nav>

            <div className="mt-6 border-t border-border pt-4">
              <div className="mb-2 flex items-baseline justify-between text-xs font-semibold text-subtle">
                <span>Section progress</span>
                <span>
                  {progress.section.answered}/{progress.section.total}
                </span>
              </div>
              <ProgressBar
                answered={progress.section.answered}
                total={progress.section.total}
                label="Section progress"
                size="sm"
                caption={false}
              />
            </div>
          </div>

          <div className="min-w-0 flex-1">
            {activeSubSection && (
              <>
                {activeSubSectionPosition !== null && (
                  <p className="mb-1 text-xs font-semibold tracking-wide text-subtle uppercase">
                    Sub-section {activeSubSectionPosition} of {data.subSections.length}
                  </p>
                )}
                <h2 className="mb-4 text-xl font-semibold text-body">{activeSubSection.title}</h2>
                <RichText html={activeSubSection.instruction} className="mb-4" />
                <QuestionList
                  subSection={activeSubSection}
                  responses={responses}
                  canEdit={editable}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  saveStatus={autosave.status}
                  onRetry={autosave.retry}
                  flaggedQuestionIds={new Set(visibleUnansweredRequired)}
                  highlightedQuestionId={highlightedQuestionId}
                  hiddenQuestionIds={hidden}
                  revisions={revisions}
                  supportingTexts={supportingTexts}
                />
              </>
            )}

            {strandedWithoutLock && !completion.loading && (
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => navigate(data!.overviewPath)}
                  className="rounded-button border border-primary bg-surface-page px-4 py-2 text-sm font-medium text-primary hover:bg-primary hover:text-primary-on"
                >
                  Back to overview
                </button>
              </div>
            )}

            {showCompletionActions && (
              <div className="mt-6">
                {isComplete ? (
                  <div
                    role="status"
                    className="rounded-lg border border-primary bg-primary/10 p-4 text-sm text-body"
                  >
                    <p className="font-semibold">This section is complete.</p>
                    <p className="mt-0.5 text-subtle">
                      Re-open it to change your answers before submitting.
                    </p>
                    <button
                      type="button"
                      onClick={() => completion.reopen()}
                      disabled={completion.pending}
                      className="mt-3 rounded-button border border-primary bg-surface-page px-4 py-2 text-sm font-medium text-primary hover:bg-primary hover:text-primary-on disabled:opacity-60"
                    >
                      Re-open to edit
                    </button>
                  </div>
                ) : (
                  // Done editing is the always-available exit that doesn't gate on
                  // completion; Mark complete lives in the top action bar.
                  <DoneEditing
                    organizationSurveyId={organizationSurveyId}
                    getResponses={() => withoutHidden(responsesRef.current, hiddenRef.current)}
                    dirtyIds={dirty.current}
                    isClean={autosave.isClean}
                    flush={autosave.flush}
                    release={sectionLock.release}
                    onExit={() => navigate(data!.overviewPath)}
                    onSavingChange={setExiting}
                    disabled={completion.pending || submitting}
                  />
                )}

                {/* Scroll target: the actions sit at the foot of a long section, so an
                  error raised by Mark complete can land below the fold. */}
                <div ref={alertsRef}>
                  {saveError && (
                    <div role="alert" className="mt-3 text-sm text-danger">
                      Couldn’t save your answers. Please try again.
                    </div>
                  )}

                  {visibleUnansweredRequired.length > 0 && (
                    <div role="alert" className="mt-3 text-sm text-danger">
                      <p>
                        {visibleUnansweredRequired.length} required question
                        {visibleUnansweredRequired.length === 1 ? '' : 's'} still need
                        {visibleUnansweredRequired.length === 1 ? 's' : ''} an answer before this
                        section can be marked complete.
                      </p>
                      {data.subSections.length > 1 && (
                        <ul className="mt-1 list-disc pl-5">
                          {data.subSections
                            .filter((sub) => unansweredBySubSection.has(sub.id))
                            .map((sub) => (
                              <li key={sub.id}>
                                {unansweredBySubSection.get(sub.id)} in {sub.title}
                              </li>
                            ))}
                        </ul>
                      )}
                      <button
                        type="button"
                        onClick={handleJumpToFirst}
                        className="mt-2 font-medium underline hover:no-underline"
                      >
                        Jump to first
                      </button>
                    </div>
                  )}

                  {completion.error && (
                    <div role="alert" className="mt-3 text-sm text-danger">
                      Couldn’t update this section. Please try again.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  )
}
